import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom-v5';
import { formatAddress } from 'spw-components/address-autocomplete/utils';
import {
  BENCHMARK_STATUS,
  BILLABLE_STATUS,
  NO_DATA_TO_SHOW,
  PROJECT_TYPE_TO_PATH,
} from 'spw-constants';
import { IProject } from 'spw-redux/modules/projects/types';
import { getAriaLabelForSensitiveInfo } from 'spw-shared/utils';
import { colors } from 'spw-theme';
import styled from 'styled-components';

type Props = {
  project: IProject;
  noLinkToProject?: boolean;
  isHeader?: boolean;
  dataTestId?: string;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start !important;
  width: 165px;

  .info {
    font-size: 14px;
    line-height: 20px;
    max-height: 20px;
    overflow: hidden;
    max-width: 220px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  a {
    color: ${colors.mainBlue};
  }

  .secondary-info {
    font-size: 12;
    line-height: '16px';
    color: ${colors.mainGray};
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;

  .info {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    color: ${colors.black};
  }

  .secondary-info {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: ${colors.mainGray};

    .secondary-info-field {
      margin-right: 6px;
    }
  }
`;

const ProjectDescription = ({
  project,
  noLinkToProject,
  isHeader,
  dataTestId,
}: Props) => {
  const location = useLocation();
  const isOverview = location.pathname.endsWith('/project-profile');
  const secondaryInfoFields = useMemo(() => {
    if (!project) return [];
    return [
      formatAddress(project),
      [BILLABLE_STATUS, BENCHMARK_STATUS].includes(project.status)
        ? project.projectNumber
        : project.status?.toUpperCase(),
    ].filter((f) => f && f !== NO_DATA_TO_SHOW);
  }, [project]);

  if (!project) return null;

  if (isHeader) {
    return (
      <HeaderContainer>
        {!isOverview && (
          <>
            <div
              className="info"
              aria-label={getAriaLabelForSensitiveInfo({
                context: 'Project List Item',
                desc: 'Project Name',
                id: project.id,
              })}
            >
              {project.projectName}
            </div>
            <div className="secondary-info">
              {secondaryInfoFields.length === 0
                ? NO_DATA_TO_SHOW
                : secondaryInfoFields.map((field) => (
                    <span
                      key={field}
                      className="secondary-info-field"
                      aria-label={getAriaLabelForSensitiveInfo({
                        context: 'Project List Item',
                        desc: 'Project Number',
                        id: project.id,
                      })}
                    >
                      {field}
                    </span>
                  ))}
            </div>
          </>
        )}
      </HeaderContainer>
    );
  }

  return (
    <Container data-testid={dataTestId}>
      {noLinkToProject ? (
        <div
          className="info"
          aria-label={getAriaLabelForSensitiveInfo({
            context: 'Project List Item',
            desc: 'Project Name',
            id: project.id,
          })}
        >
          {project.projectName}
        </div>
      ) : (
        <Link
          to={`/projects/${PROJECT_TYPE_TO_PATH[project.type]}/${
            project.id
          }/project-profile`}
        >
          <div
            className="info"
            aria-label={getAriaLabelForSensitiveInfo({
              context: 'Project List Item',
              desc: 'Project Name',
              id: project.id,
            })}
          >
            {project.projectName}
          </div>
        </Link>
      )}

      <div
        className="secondary-info"
        aria-label={getAriaLabelForSensitiveInfo({
          context: 'Project List Item',
          desc: 'Project Number',
          id: project.id,
        })}
      >
        {project.projectNumber}
      </div>
    </Container>
  );
};

export default ProjectDescription;
